import { lazy, Suspense, useEffect, useLayoutEffect, useState } from "react";
import SmoothScroll from "../components/SmoothScroll";
import MapPhase from "../components/MapPhase";
import ErrorOverlay, { CanvasErrorBoundary } from "../components/ErrorOverlay";
import LoadingOverlay from "../components/LoadingOverlay";
import PhaseTransitionOverlay from "../components/PhaseTransitionOverlay";
import ScrollProgress from "../components/ScrollProgress";
import { scrollState, onPhaseChange, resetScrollState, setPhase, hasSeenIntro, markIntroSeen } from "../state/scroll";
import ConstructionPhase from "../components/ConstructionPhase";
import ReservationPhase from "../components/ReservationPhase";
// import JourneyPhase from "../components/JourneyPhase"; // cut — see HomePage render notes
import DayAtNausiddhi from "../components/DayAtNausiddhi";
import HomeNavbar from "../components/HomeNavbar";
import Footer from "../components/site/Footer";

// The 3D globe intro (Three.js + react-three-fiber + drei ≈ 276KB gzipped)
// is only ever shown on a FIRST visit — return visits jump straight to PLOT
// where the canvas is permanently hidden. Lazy-loading it means:
//   • return visitors never download the Three.js chunk at all, and
//   • first visitors defer it out of the initial homepage bundle so the
//     rest of the page (nav, copy, construction frames) can boot sooner.
// LoadingOverlay / ReturnCover already mask the brief async gap, so the
// Suspense fallback is intentionally null.
const Experience = lazy(() => import("../components/Experience"));

// Only mount the heavy 3D Experience when the intro will actually play.
function IntroExperience() {
  // hasSeenIntro() is stable for the life of this mount (it only flips to
  // true once PLOT is first reached, at which point the canvas is hidden
  // anyway), so a mount-time decision is correct and avoids re-fetching.
  if (hasSeenIntro()) return null;
  return (
    <Suspense fallback={null}>
      <Experience />
    </Suspense>
  );
}

// Dark cover that shows instantly on mount, hides once the 3D scene has
// actually rendered its first textured frame. Listens for `scene-painted`
// (emitted from inside the Canvas via useFrame) rather than `canvas-ready`
// (which fires when the GL context exists but textures may not be uploaded
// yet — that left a black flash on return visits).
// On return visits there is no 3D canvas to cover (canvas is hidden during
// PLOT after the 3D-land removal), so we fade out immediately instead of
// waiting for scene-painted (which would never fire).
function ReturnCover() {
  const [visible, setVisible] = useState(true);
  const [fadeOut, setFadeOut] = useState(false);

  useEffect(() => {
    if (!hasSeenIntro()) {
      // First visit — nothing to cover yet; hide immediately.
      setVisible(false);
      return;
    }
    // Return visit — dismiss straight away (no 3D flash to mask).
    setFadeOut(true);
    const t = setTimeout(() => setVisible(false), 500);
    return () => clearTimeout(t);
  }, []);

  if (!visible) return null;

  return (
    <div
      aria-hidden="true"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9998,
        background: "#04110a",
        pointerEvents: "none",
        opacity: fadeOut ? 0 : 1,
        transition: "opacity 500ms ease",
      }}
    />
  );
}
// ScrollTrack provides 600vh of scroll height for INTRO/MAP/JOURNEY phases.
// Hidden during PLOT — real page content (ConstructionPhase etc.) provides height.
function ScrollTrack() {
  const [phase, setLocalPhase] = useState(scrollState.phase);
  useEffect(() => onPhaseChange(setLocalPhase), []);
  // Keep in DOM during JOURNEY (scroll height must stay intact).
  // Fully remove during PLOT and INTRO (INTRO is locked anyway).
  if (phase === "PLOT") return null;
  return (
    <div
      className="scroll-track"
      aria-hidden="true"
      style={phase === "JOURNEY" ? { pointerEvents: "none" } : undefined}
    />
  );
}

// Footer shows once user reaches PLOT (the scrollable content phase).
function HomeFooter() {
  const [phase, setLocalPhase] = useState(scrollState.phase);
  useEffect(() => onPhaseChange(setLocalPhase), []);
  if (phase !== "PLOT") return null;
  return <Footer />;
}

const SESSION_KEY = "nr_seen_intro"; // unused now, kept for reference

export default function HomePage() {
  // useLayoutEffect, NOT useEffect — must run BEFORE children's effects so
  // SmoothScroll sees the correct phase when it mounts Lenis. With the old
  // useEffect, child effects ran first, SmoothScroll captured `lastPhase`
  // as the stale "INTRO" module default, and the subsequent INTRO→PLOT
  // listener firing was sometimes lost across the render boundary.
  useLayoutEffect(() => {
    if (hasSeenIntro()) {
      // Returning via SPA navigation — skip INTRO, land in PLOT immediately.
      window.scrollTo(0, 0);
      // Hard-clear any leftover scroll lock from a previous mount cleanup.
      document.documentElement.style.overflow = "";
      document.body.style.overflow = "";
      scrollState.phase = "PLOT";
    } else {
      // Fresh page load — full cinematic experience from INTRO.
      window.scrollTo(0, 0);
      resetScrollState();
    }
  }, []);

  // Belt-and-braces: AFTER all child effects have run, ensure scroll is
  // genuinely unlocked on return visits. Some component (ConstructionPhase
  // RAF, leftover wheel blocker) can occasionally re-lock.
  useEffect(() => {
    if (!hasSeenIntro()) return;
    document.documentElement.style.overflow = "";
    document.body.style.overflow = "";
    setPhase("PLOT");
    const lenis = window.__lenis;
    if (lenis) { try { lenis.start(); } catch (_) {} }
    // Double-rAF: defer until page content (DayAtNausiddhi + ConstructionPhase) is in DOM
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        window.dispatchEvent(new CustomEvent("plot-dwell-end"));
      });
    });
  }, []);

  // Mark intro seen the first time user reaches PLOT.
  useEffect(() => {
    const off = onPhaseChange((p) => {
      if (p === "PLOT") markIntroSeen();
    });
    return off;
  }, []);

  return (
    <div
      className="relative bg-ink-950 text-white"
      style={{ minHeight: "100vh", background: "#04110a" }}
    >
      <ReturnCover />
      <LoadingOverlay />
      <ErrorOverlay />
      <SmoothScroll />
      <CanvasErrorBoundary>
        <IntroExperience />
      </CanvasErrorBoundary>
      <MapPhase />
      {/* <JourneyPhase /> — cut per product decision (built, then scrapped;
          this is the second time). Map hands off straight to PLOT now.
          Component file kept intact in case it's wanted again. */}
      <DayAtNausiddhi />
      <ConstructionPhase />
      <ReservationPhase />
      <HomeFooter />
      <HomeNavbar />
      <ScrollTrack />
      <ScrollProgress />
      <PhaseTransitionOverlay />
    </div>
  );
}
